export type DocumentSortMode = "name" | "added" | "document_date";

export type SortableDocument = {
  name: string;
  createdAt?: string | null;
  keyDates?: Record<string, string> | null;
};

export const DOCUMENT_SORT_OPTIONS: { id: DocumentSortMode; label: string }[] = [
  { id: "added", label: "Added" },
  { id: "document_date", label: "Date" },
  { id: "name", label: "Name" },
];

export function documentDateForSort(doc: SortableDocument): string | null {
  const keyDates = doc.keyDates;
  if (!keyDates) return null;
  if (keyDates.document_date?.trim()) return keyDates.document_date.trim();
  const first = Object.values(keyDates).find((value) => value && String(value).trim());
  return first ? String(first).trim() : null;
}

function parseSortTime(iso: string | null | undefined): number {
  if (!iso?.trim()) return Number.NaN;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.NaN : t;
}

export function sortDocuments<T extends SortableDocument>(docs: T[], mode: DocumentSortMode): T[] {
  const sorted = [...docs];
  if (mode === "name") {
    sorted.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    return sorted;
  }

  if (mode === "added") {
    sorted.sort((a, b) => {
      const ta = parseSortTime(a.createdAt);
      const tb = parseSortTime(b.createdAt);
      const aMissing = Number.isNaN(ta);
      const bMissing = Number.isNaN(tb);
      if (aMissing && bMissing) return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
      if (aMissing) return 1;
      if (bMissing) return -1;
      return tb - ta;
    });
    return sorted;
  }

  sorted.sort((a, b) => {
    const ta = parseSortTime(documentDateForSort(a));
    const tb = parseSortTime(documentDateForSort(b));
    const aMissing = Number.isNaN(ta);
    const bMissing = Number.isNaN(tb);
    if (aMissing && bMissing) return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    if (aMissing) return 1;
    if (bMissing) return -1;
    return ta - tb;
  });
  return sorted;
}

export function formatDocumentListDateLabel(doc: SortableDocument): string | null {
  return formatDocumentListSubtitle(doc);
}

function datePrefixFromRaw(raw: string): string {
  const trimmed = raw.trim();
  if (DATE_ONLY_RE.test(trimmed)) return trimmed;
  const head = trimmed.slice(0, 10);
  if (DATE_ONLY_RE.test(head)) return head;
  return trimmed;
}

export function formatDocumentListTitle(doc: SortableDocument): string {
  const docDate = documentDateForSort(doc);
  if (docDate) {
    return `${datePrefixFromRaw(docDate)} ${doc.name}`;
  }
  return doc.name;
}

export function formatDocumentListSubtitle(doc: SortableDocument): string | null {
  const docDate = documentDateForSort(doc);
  const addedPart = doc.createdAt ? `Added ${formatShortDate(doc.createdAt)}` : null;
  if (docDate) {
    const dated = `Dated to ${formatShortDate(docDate)}`;
    return addedPart ? `${dated} - ${addedPart}` : dated;
  }
  return addedPart;
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function formatShortDate(iso: string): string {
  const trimmed = iso.trim();
  if (DATE_ONLY_RE.test(trimmed)) {
    const [year, month, day] = trimmed.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  }
  const t = Date.parse(trimmed);
  if (Number.isNaN(t)) return trimmed;
  return new Date(t).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
